export interface CalendarEvent {
	attendees: string[];
	bookingServiceName: string;
	description?: string;
	location?: string;
	startDate: Date;
	endDate: Date;
	organizerName?: string;
	organizerEmail?: string;
}

/**
 * Formats a date for ICS format
 */
function formatICSDate(date: Date): string {
	return date
		.toISOString()
		.replace(/[-:]/g, '')
		.replace(/\.\d{3}/, '');
}

/**
 * Escapes special characters in ICS text fields
 */
function escapeICSText(text: string): string {
	return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n').replace(/\r/g, '');
}

/**
 * Folds a line to comply with RFC 5545 line length limit (75 characters)
 * Lines longer than 75 characters are folded by inserting CRLF+SPACE
 */
function foldLine(line: string): string {
	const maxLength = 75;
	if (line.length <= maxLength) {
		return line;
	}

	let result = '';
	let start = 0;

	while (start < line.length) {
		const end = start + maxLength;
		const chunk = line.substring(start, end);

		if (start === 0) {
			result += chunk;
		} else {
			result += '\r\n ' + chunk; // RFC 5545: continuation with CRLF+SPACE
		}

		start = end;
	}

	return result;
}

/**
 * Generates ICS file content for a calendar event
 */
export function generateICS(event: CalendarEvent): string {
	const now = new Date();
	const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@booking.meisterplan.com`;

	let ics = '';
	// cspell:disable
	ics += foldLine('BEGIN:VCALENDAR') + '\r\n';
	ics += foldLine('VERSION:2.0') + '\r\n';
	ics += foldLine('PRODID:-//Meisterplan//Booking Component//EN') + '\r\n';
	ics += foldLine('BEGIN:VEVENT') + '\r\n';
	ics += foldLine(`UID:${uid}`) + '\r\n';
	ics += foldLine(`DTSTAMP:${formatICSDate(now)}`) + '\r\n';
	ics += foldLine(`DTSTART:${formatICSDate(event.startDate)}`) + '\r\n';
	ics += foldLine(`DTEND:${formatICSDate(event.endDate)}`) + '\r\n';
	// cspell:enable
	ics += foldLine(`SUMMARY:${escapeICSText(event.bookingServiceName)}`) + '\r\n';

	if (event.description) {
		ics += foldLine(`DESCRIPTION:${escapeICSText(event.description)}`) + '\r\n';
	}

	if (event.location) {
		ics += foldLine(`LOCATION:${escapeICSText(event.location)}`) + '\r\n';
	}

	if (event.organizerName && event.organizerEmail) {
		ics += foldLine(`ORGANIZER;CN=${escapeICSText(event.organizerName)}:mailto:${event.organizerEmail}`) + '\r\n';
	}

	for (const emailAddress of event.attendees) {
		// cspell:disable-next-line
		ics += foldLine(`ATTENDEE;ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE:mailto:${emailAddress}`) + '\r\n';
	}

	// Add reminder 30 minutes before the event
	// cspell:disable-next-line
	ics += foldLine('BEGIN:VALARM') + '\r\n';
	ics += foldLine('TRIGGER:-PT30M') + '\r\n';
	ics += foldLine('ACTION:DISPLAY') + '\r\n';
	ics += foldLine(`DESCRIPTION:Reminder: ${escapeICSText(event.bookingServiceName)}`) + '\r\n';
	// cspell:disable-next-line
	ics += foldLine('END:VALARM') + '\r\n';

	// cspell:disable-next-line
	ics += foldLine('END:VEVENT') + '\r\n';
	// cspell:disable-next-line
	ics += foldLine('END:VCALENDAR') + '\r\n';

	return ics;
}

/**
 * Downloads an ICS file with the given event data
 */
export function downloadICSFile(event: CalendarEvent, filename?: string): void {
	const icsContent = generateICS(event);
	const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
	const url = window.URL.createObjectURL(blob);

	const link = document.createElement('a');
	link.href = url;
	link.download = filename || `appointment-${formatICSDate(event.startDate).slice(0, 8)}.ics`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	window.URL.revokeObjectURL(url);
}
